import { CommonModule } from '@angular/common';
import { Component, Input, NgZone, OnChanges, SimpleChanges, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, Subject, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { browserRenderableExtensions } from 'src/app/consts';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { ConversionResponse } from 'src/app/services/convert-raw-preview.service';
import { ConvertRawService } from 'src/app/services/convert-raw.service';
import { ConvertWebImageService } from 'src/app/services/convert-web-image.service';
import { StateService } from 'src/app/services/state.service';
import { StoryblokService } from 'src/app/services/storyblok.service';
import { DropComponent } from 'src/app/shared-extra/components/drop/drop.component';
import { SvgComponent } from 'src/app/shared/components/svg/svg.component';
import { ButtonDirective } from 'src/app/shared/directives/button.directive';
import { ResizeDirective } from 'src/app/shared/directives/resize.directive';
import { TooltipDirective } from 'src/app/shared/directives/tooltip.directive';
import { TransitionPreventorDirective } from 'src/app/shared/directives/transition-preventor.directive';
import { BlockStyle, BlockWrapperComponent } from 'src/app/storyblok/components/block-wrapper/block-wrapper.component';
import * as wasm from 'wasm-zip';
import { ContextMenuComponent } from '../../../../shared/components/context-menu/context-menu.component';
import { LoadingIndicatorComponent } from '../../../../shared/components/loading-indicator/loading-indicator.component';
import { DataUnitsPipe } from '../../../../shared/pipes/data-units.pipe';
import { ConversionFormat } from '../../templates/conversion-template/conversion-template.component';
import { ConversionFormatSelectMenuContentComponent } from '../conversion-format-select-menu-content/conversion-format-select-menu-content.component';
import { DotRectanglesComponent } from '../dot-rectangles/dot-rectangles.component';

export type FileConversion = {
  file: File;
  result?: ConversionResponse;
  status: 'idle' | 'pending' | 'processing' | 'done' | 'error';
  toFormat: string;
};

@Component({
  selector: 'app-convert',
  standalone: true,
  templateUrl: './convert.component.html',
  styleUrls: ['./convert.component.scss'],
  imports: [
    CommonModule,
    BlockWrapperComponent,
    DropComponent,
    ButtonDirective,
    SvgComponent,
    LoadingIndicatorComponent,
    ResizeDirective,
    DataUnitsPipe,
    ContextMenuComponent,
    TooltipDirective,
    ConversionFormatSelectMenuContentComponent,
    DotRectanglesComponent,
    FormsModule,
    ReactiveFormsModule,
    TransitionPreventorDirective,
  ],
})
export class ConvertComponent implements OnChanges {
  @Input() _uid: string; // just here to prevent errors
  @Input() component: string; // just here to prevent errors
  @Input() _editable: string;
  @Input() replacer?: (key: string) => string;
  @Input() style: BlockStyle[];
  @Input() fromFormat?: ConversionFormat;
  @Input() toFormat?: ConversionFormat;

  private convertRawService = inject(ConvertRawService);
  private convertWebImageService = inject(ConvertWebImageService);
  public state = inject(StateService);
  private zone = inject(NgZone);
  private router = inject(Router);
  private storyBlokService = inject(StoryblokService);
  private analytics = inject(AnalyticsService);

  qualityForm = new FormGroup({
    quality: new FormControl(95, [Validators.required, Validators.min(1), Validators.max(100)]),
  });
  savingAll = false;
  rawAccelleration = true;

  private newFiles$ = new Subject<File[]>();

  private onNewFiles = this.newFiles$.subscribe(files => {
    const totalSize = files.reduce((total, file) => total + file.size, 0);

    const alreadyHadFiles = this.state.conversionCache.length > 0;

    this.state.conversionCache.push(
      ...files.map(file => ({ file, status: 'idle' as const, toFormat: this.toFormat?.slug }))
    );

    this.analytics.track('convert_selected_files', false, {
      count: files.length,
      totalSize: totalSize,
      ...this.getFileTotals(files),
      sourceFormat: this.getConversionSourceFormat(),
      isMultiSourceFormat: this.isMultiSourceFormat(),
      destinationFormat: this.getConversionDestinationFormat(),
      isMultiDestinationFormat: this.isMultiDestinationFormat(),
    });

    this.convertRawService.preloadWorkers();

    if (alreadyHadFiles) return;
    this.storyBlokService.getStories({ content_type: 'ImageFormat' }).subscribe((formats: ConversionFormat[]) => {
      const newExtension = files
        .find(file => {
          const extension = file.name.split('.').pop().toLowerCase();
          return formats.find(format => format.slug === extension);
        })
        ?.name.split('.')
        .pop()
        .toLowerCase();

      if (!newExtension) return;
      if (newExtension !== this.fromFormat?.slug) {
        if (this.toFormat) {
          this.router.navigate(['/convert', `${newExtension}-to-${this.toFormat.slug}`]);
        } else {
          this.router.navigate(['/convert', `${newExtension}`]);
        }
      }
    });
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.toFormat) {
      this.state.conversionCache.forEach(conversion => {
        conversion.toFormat = this.toFormat?.slug;
        if (conversion.status === 'done' || conversion.status === 'error') {
          delete conversion.result;
          conversion.status = 'idle';
        }
      });
    }
  }

  onSelectFilesFromTarget(target: EventTarget) {
    return this.onSelectFiles(Array.from((target as HTMLInputElement).files));
  }

  onSelectFiles(files: File[]) {
    this.newFiles$.next(files);
  }

  removeConversion(conversion: FileConversion) {
    if (conversion.result?.src) {
      URL.revokeObjectURL(conversion.result.src);
    }
    this.state.conversionCache.splice(this.state.conversionCache.indexOf(conversion), 1);
  }

  reset() {
    this.state.conversionCache.forEach(conversion => {
      if (conversion.result?.src) {
        URL.revokeObjectURL(conversion.result.src);
      }
    });
    this.state.conversionCache = [];
  }

  convertAll() {
    const startTime = Date.now();
    forkJoin(
      this.state.conversionCache.map(conversion => {
        if (conversion.status === 'idle') {
          return this.convertConversion(conversion);
        }
        return of(null);
      })
    ).subscribe(results => {
      const conversions = results.filter(result => !!result && !('status' in result)) as ConversionResponse[];
      this.analytics.track('convert_conversion_results', false, {
        totalTime: Date.now() - startTime,
        ...this.getConversionResultStats(conversions),
        sourceFormat: this.getConversionSourceFormat(),
        isMultiSourceFormat: this.isMultiSourceFormat(),
        destinationFormat: this.getConversionDestinationFormat(),
        isMultiDestinationFormat: this.isMultiDestinationFormat(),
      });
    });
  }

  private convertConversion(conversion: FileConversion) {
    const quality = this.qualityForm.get('quality').value / 100;
    if (!conversion.toFormat) return;
    conversion.status = 'pending';
    let startTime: number;
    return this.zone.runOutsideAngular(() => {
      return of(null).pipe(
        switchMap(() => {
          if (this.convertWebImageService.canConvertFile(conversion.file, conversion.toFormat)) {
            return this.convertWebImageService.convertFile(conversion.file, conversion.toFormat, quality);
          }
          return this.convertRawService.convertFile(
            conversion.file,
            conversion.toFormat,
            quality,
            this.rawAccelleration
          );
        }),
        tap(result => {
          this.zone.runGuarded(() => {
            if ('status' in result) {
              conversion.status = 'processing';
              if (!startTime) startTime = Date.now();
            }
            if ('file' in result) {
              conversion.status = result.error ? 'error' : 'done';
              conversion.result = result;
              conversion.result.timeTaken = Date.now() - startTime;
            }
          });
        })
      );
    });
  }

  showConvertAllButton() {
    return this.state.conversionCache.some(conversion => conversion.status === 'idle');
  }

  showLoadingAllButton() {
    return this.state.conversionCache.some(
      conversion => conversion.status === 'pending' || conversion.status === 'processing'
    );
  }

  private saveSingleFile = (conversion: FileConversion) => {
    const a = document.createElement('a');
    a.href = conversion.result.src;
    a.download = conversion.result.outputName;
    a.click();
  };

  async saveAll() {
    const conversions = this.state.conversionCache.filter(conversion => conversion.result && !conversion.result.error);
    if (conversions.length === 0) return;
    if (conversions.length === 1) return this.saveSingleFile(conversions[0]);

    this.savingAll = true;

    const directoryMapping: { [filename: string]: Uint8Array } = {};

    for (const conversion of this.state.conversionCache) {
      const url = conversion.result.src;
      const resp = await fetch(url);
      const arrayBuffer = await resp.arrayBuffer();
      directoryMapping[conversion.result.outputName] = new Uint8Array(arrayBuffer);
    }

    const zipBinary = wasm.generate_zip_binary(directoryMapping);

    const downloadableBlob = new Blob([zipBinary], {
      type: 'application/zip;charset=utf-8',
    });

    const blobURL = URL.createObjectURL(downloadableBlob);
    const a = document.createElement('a');
    a.href = blobURL;
    a.download = 'converted-images.zip';
    a.click();
    this.savingAll = false;
    URL.revokeObjectURL(blobURL);
  }

  canRenderConversion(conversion: FileConversion) {
    if (!conversion.result) return false;
    if (conversion.result.error) return false;
    if (conversion.result.outputSize > 2_500_000) return false;
    const extension = conversion.result.outputName.split('.').pop().toLowerCase();
    return browserRenderableExtensions.includes(extension);
  }

  getFormat(formatSlug: string): Observable<ConversionFormat> {
    return this.storyBlokService.getStories({ content_type: 'ImageFormat', starts_with: formatSlug }).pipe(
      map((formats: ConversionFormat[]) => {
        return formats.find(format => format.slug === formatSlug);
      })
    );
  }

  private isMultiSourceFormat() {
    const formats: Set<string> = new Set();
    this.state.conversionCache.forEach(conversion => {
      if (conversion.toFormat) formats.add(conversion.file.name.split('.').pop().toLowerCase());
    });
    return formats.size > 1;
  }

  private isMultiDestinationFormat() {
    const formats: Set<string> = new Set();
    this.state.conversionCache.forEach(conversion => {
      if (conversion.toFormat) formats.add(conversion.toFormat);
    });
    return formats.size > 1;
  }

  private getConversionSourceFormat() {
    const formats = this.state.conversionCache.map(conversion => conversion.file.name.split('.').pop().toLowerCase());

    const counts = formats.reduce((counts, format) => {
      counts[format] = (counts[format] || 0) + 1;
      return counts;
    }, {} as { [key: string]: number });

    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'unknown';
  }

  private getConversionDestinationFormat() {
    const formats = this.state.conversionCache.map(conversion => conversion.toFormat);
    const counts = formats.reduce((counts, format) => {
      counts[format] = (counts[format] || 0) + 1;
      return counts;
    }, {} as { [key: string]: number });
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'unknown';
  }

  private shouldSwitchGlobalToFormat() {
    if (!this.fromFormat) return false;
    if (!this.toFormat) return true;
    const areSomeConversionsTargetingCurrentToFormat = this.state.conversionCache.some(
      conversion => conversion.toFormat === this.toFormat.slug
    );
    return !areSomeConversionsTargetingCurrentToFormat;
  }

  switchConversionToFormat(conversion: FileConversion, toFormat: ConversionFormat) {
    conversion.toFormat = toFormat.slug;

    if (this.shouldSwitchGlobalToFormat()) {
      this.router.navigate(['/convert', `${this.fromFormat?.slug}-to-${toFormat.slug}`]);
    }
  }

  private getConversionResultStats(conversions: ConversionResponse[]) {
    const successes: { [key: string]: number } = {};
    const failures: { [key: string]: number } = {};
    const totalTimes: { [key: string]: number } = {};
    const resolutions: { [key: string]: number } = {};

    conversions.forEach(conversion => {
      const succeeded = !conversion.error;
      const extension = conversion.file.name.split('.').pop().toLowerCase();
      const toExtension = conversion.targetFormat;
      const key = `${extension}-to-${toExtension}`;

      if (succeeded) {
        successes[key] = (successes[key] || 0) + 1;
        totalTimes[key] = (totalTimes[key] || 0) + conversion.timeTaken;
        const resolution = `${conversion.outputWidth}x${conversion.outputHeight}`;
        resolutions[resolution] = (resolutions[resolution] || 0) + 1;
      } else failures[key] = (failures[key] || 0) + 1;
    });

    const totalSuccesses = Object.values(successes).reduce((total, count) => total + count, 0);
    const totalFailures = Object.values(failures).reduce((total, count) => total + count, 0);

    return {
      failures,
      successes,
      totalSuccesses,
      totalFailures,
      totalTimes,
      resolutions,
    };
  }
  private getFileTotals(files: File[]) {
    const counts: { [key: string]: number } = {};
    const totalSizes: { [key: string]: number } = {};

    files.forEach(file => {
      const extension = file.name.split('.').pop().toLowerCase();
      counts[extension] = (counts[extension] || 0) + 1;
      totalSizes[extension] = (totalSizes[extension] || 0) + file.size;
    });
    return {
      counts,
      totalSizes,
    };
  }

  canConvert() {
    if (this.toFormat) return true;
    return this.state.conversionCache.every(conversion => conversion.toFormat);
  }

  trackFn(index: number, conversion: FileConversion) {
    return conversion.file;
  }
}
